import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const GlobalLoadingContext = createContext();

export const useGlobalLoading = () => {
  const context = useContext(GlobalLoadingContext);
  if (!context) {
    throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider');
  }
  return context;
};

export const GlobalLoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);

  const startLoading = useCallback((isQuizSubmission = false) => {
    setIsLoading(true);
    // Add disabled class to body
    document.body.classList.add('global-loading-active');
    
    // If it's a quiz submission, also disable aside components
    if (isQuizSubmission) {
      document.body.classList.add('quiz-submission-active');
      console.log('Quiz submission detected - disabling aside components');
    }
    
    // Function to disable buttons with retry mechanism
    const disableButtons = () => {
      const allButtons = document.querySelectorAll('button');
      let buttonsDisabled = 0;
      
      allButtons.forEach(button => {
        const buttonText = button.textContent?.trim().toLowerCase() || '';
        const isDisabledButton = buttonText.includes('back') ||
                                buttonText.includes('exit') ||
                                button.getAttribute('data-dismiss') ||
                                button.getAttribute('data-bs-dismiss') ||
                                button.className?.toLowerCase().includes('close') ||
                                button.className?.toLowerCase().includes('exit') ||
                                button.className?.toLowerCase().includes('back');
        
        if (isDisabledButton) {
          button.classList.add('exit-button-disabled');
          buttonsDisabled++;
        }
      });
      
      console.log(`Disabled ${buttonsDisabled} buttons`);
      
      // If no buttons were disabled, retry after a short delay (for dynamically created buttons)
      if (buttonsDisabled === 0) {
        setTimeout(() => {
          const retryButtons = document.querySelectorAll('button');
          retryButtons.forEach(button => {
            const buttonText = button.textContent?.trim().toLowerCase() || '';
            const isDisabledButton = buttonText.includes('back') ||
                                    buttonText.includes('exit') ||
                                    button.getAttribute('data-dismiss') ||
                                    button.getAttribute('data-bs-dismiss') ||
                                    button.className?.toLowerCase().includes('close') ||
                                    button.className?.toLowerCase().includes('exit') ||
                                    button.className?.toLowerCase().includes('back');
            
            if (isDisabledButton) {
              button.classList.add('exit-button-disabled');
            }
          });
        }, 100);
      }
    };
    
    // Initial attempt
    disableButtons();
    
    // Also try after a longer delay for modals/alerts that might load later
    setTimeout(disableButtons, 500);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    // Remove disabled class from body
    document.body.classList.remove('global-loading-active');
    
    // Remove quiz submission class
    document.body.classList.remove('quiz-submission-active');
    
    // Remove specific class from exit buttons
    const exitButtons = document.querySelectorAll('.exit-button-disabled');
    exitButtons.forEach(button => {
      button.classList.remove('exit-button-disabled');
    });
  }, []);

  // Global click interceptor and navigation listener
  useEffect(() => {
    // Function to check and disable buttons
    const checkAndDisableButtons = () => {
      if (!isLoading) return;
      
      const allButtons = document.querySelectorAll('button');
      allButtons.forEach(button => {
        const buttonText = button.textContent?.trim().toLowerCase() || '';
        const isDisabledButton = buttonText.includes('back') ||
                                buttonText.includes('exit') ||
                                button.getAttribute('data-dismiss') ||
                                button.getAttribute('data-bs-dismiss') ||
                                button.className?.toLowerCase().includes('close') ||
                                button.className?.toLowerCase().includes('exit') ||
                                button.className?.toLowerCase().includes('back');
        
        if (isDisabledButton && !button.classList.contains('exit-button-disabled')) {
          button.classList.add('exit-button-disabled');
          console.log('Dynamically disabled button:', buttonText);
        }
      });
    };

    // Set up MutationObserver to watch for dynamically added buttons
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is a button or contains buttons
              if (node.tagName === 'BUTTON') {
                checkAndDisableButtons();
              } else if (node.querySelector && node.querySelector('button')) {
                checkAndDisableButtons();
              }
            }
          });
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also set up a periodic check for buttons (as a fallback)
    const intervalId = setInterval(() => {
      if (isLoading) {
        checkAndDisableButtons();
      }
    }, 1000); // Check every second

    const handleGlobalClick = (event) => {
      // Check if the clicked element is a button, link, or has click handlers
      const target = event.target;
      const buttonElement = target.closest('button') || target;
      const buttonText = buttonElement.textContent?.trim() || '';
      
      // Check if it's one of the specific trigger buttons
      const isTriggerButton = buttonText.toLowerCase().includes('submit') ||
                             buttonText.toLowerCase().includes('go to quiz') ||
                             buttonText.toLowerCase().includes('next') ||
                             buttonElement.type === 'submit' ||
                             buttonElement.getAttribute('type') === 'submit';
      
      // Check if it's a quiz submission (special case)
      const isQuizSubmission = buttonText.toLowerCase().includes('submit') && 
                              (window.location.pathname.includes('quiz') || 
                               window.location.pathname.includes('exam') ||
                               document.querySelector('.quiz-container') ||
                               document.querySelector('.exam-container') ||
                               document.querySelector('[class*="quiz"]') ||
                               document.querySelector('[class*="exam"]'));
      
      const isInteractive = target.tagName === 'BUTTON' || 
                           target.tagName === 'A' || 
                           target.onclick || 
                           target.getAttribute('onclick') ||
                           target.closest('button') ||
                           target.closest('a') ||
                           target.closest('[onclick]') ||
                           target.closest('[data-bs-toggle]') ||
                           target.closest('.btn') ||
                           target.closest('.nav-link') ||
                           target.closest('[role="button"]') ||
                           target.closest('.dropdown-toggle') ||
                           target.closest('.form-control') ||
                           target.closest('.form-select');

      // Only trigger global loading if it's interactive AND it's one of the specified trigger buttons
      if (isInteractive && isTriggerButton && !isLoading) {
        console.log('Trigger button clicked, starting global loading...');
        startLoading(isQuizSubmission);
        
        // Auto-stop loading after a delay (you can adjust this)
        setTimeout(() => {
          stopLoading();
        }, 3000); // 3 seconds default
      }
    };

    // Listen for navigation changes (React Router)
    const handleNavigation = () => {
      console.log('Navigation detected, stopping global loading...');
      stopLoading();
    };

    // Listen for page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page became visible, stopping global loading...');
        stopLoading();
      }
    };

    // Listen for window focus
    const handleWindowFocus = () => {
      console.log('Window focused, stopping global loading...');
      stopLoading();
    };

    // Add event listeners
    document.addEventListener('click', handleGlobalClick, true);
    window.addEventListener('popstate', handleNavigation);
    window.addEventListener('beforeunload', handleNavigation);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    // Listen for React Router navigation (if using React Router)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      handleNavigation();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      handleNavigation();
    };

    // Cleanup
    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('beforeunload', handleNavigation);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      
      // Disconnect observer
      observer.disconnect();
      
      // Clear interval
      clearInterval(intervalId);
      
      // Restore original history methods
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [isLoading, startLoading, stopLoading]);

  const value = {
    isLoading,
    startLoading,
    stopLoading
  };

  return (
    <GlobalLoadingContext.Provider value={value}>
      {children}
    </GlobalLoadingContext.Provider>
  );
};
